// @ts-nocheck
import React, { useState, useMemo } from 'react';
import { useSearch, useNavigate, useParams, Link } from '@tanstack/react-router';
import { trpcClient } from '@/lib/trpc';
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { format } from 'date-fns';
import { Box, Calendar, Check, ChevronsUpDown, Download, Printer } from 'lucide-react';
import PrintDialog from '@/components/shared/PrintDialog';
import { StockCardPrintTemplate } from '@/components/warehouse/print/InventoryReportPrintTemplates';
import { useQuery } from '@tanstack/react-query';
import { exportToExcel, type ExcelColumnDef } from '@/lib/excel.utils';

const STOCK_CARD_COLUMNS: ExcelColumnDef[] = [
  { key: 'idx', label: 'STT', width: 8 },
  { key: 'transactionDate', label: 'Ngày Tháng', width: 14 },
  { key: 'documentNo', label: 'Số Chứng Từ', width: 18 },
  { key: 'notes', label: 'Diễn Giải', width: 28 },
  { key: 'inQty', label: 'Nhập Kho', width: 14, format: 'number' },
  { key: 'outQty', label: 'Xuất Kho', width: 14, format: 'number' },
  { key: 'balance', label: 'Tồn Lũy Kế', width: 14, format: 'number' },
];
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { cn } from '@/lib/utils';
import { packingLabel } from '@apv-erp/status';
import { ListPageShell } from '@/components/shared/ListPageShell';
import { useHeaderSearch } from '@/hooks/useHeaderSearch';

import { toVnDateString, vnStartOfMonth, resolveWarehouseDomain, warehouseDomainOf, WAREHOUSE_DOMAINS } from "@apv-erp/status";
import { usePageSizeState } from "@/hooks/usePreferredPageSize";
export function cleanMaterialDisplayName(rawName?: string): string {
  if (!rawName) return '—';
  const cleaned = String(rawName)
    .replace(/^material_subtypes_[A-Za-z0-9_]+\s*/i, '')
    .trim();
  return cleaned || String(rawName).trim();
}

export default function StockCardPage() {
  const search = useSearch({ strict: false });
  const navigate = useNavigate();
  const { warehouseType } = useParams({ strict: false }) as any;
  const urlParams = typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : new URLSearchParams();
  const itemType = (search?.type as string) || urlParams.get('type') || warehouseType?.toUpperCase() || 'MATERIAL';
  // Nhóm kho của trang: danh mục hàng + danh sách kho đúng nhóm (trước đây kho kỹ thuật 'TECH' không tải danh mục nào)
  const domain = resolveWarehouseDomain(itemType) || 'MATERIAL';
  const domainDef = WAREHOUSE_DOMAINS[domain];
  const [printOpen, setPrintOpen] = useState(false);
  
  // States for filters
  const [localItemId, setLocalItemId] = useState(search?.itemId as string || urlParams.get('itemId') || '');
  const [warehouseId, setWarehouseId] = useState(search?.warehouseId as string || urlParams.get('warehouseId') || 'ALL');
  const [startDate, setStartDate] = useState(() => vnStartOfMonth());
  const [endDate, setEndDate] = useState(() => toVnDateString());
  const [openCombobox, setOpenCombobox] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = usePageSizeState(50);

  const { headerSearch } = useHeaderSearch();

  React.useEffect(() => {
    setPage(1);
  }, [headerSearch, localItemId, warehouseId, startDate, endDate]);

  // 1. Fetch Warehouses
  const { data: warehouseData } = useQuery({
    queryKey: ['warehouses', 'list'],
    queryFn: () => trpcClient.warehouses.list.query({ limit: 100 })
  });
  const warehouses = (warehouseData?.items || []).filter((w: any) => {
    const d = warehouseDomainOf(w.type);
    return d === domain || d === null;
  });

  // 2. Fetch Items based on itemType
  const { data: materialsData } = useQuery({
    queryKey: ['materials', 'list'],
    queryFn: () => trpcClient.materials.list.query({ limit: 1000 }),
    enabled: domain === 'MATERIAL' || domain === 'SCRAP',
  });
  const { data: productsData } = useQuery({
    queryKey: ['products', 'list'],
    queryFn: () => trpcClient.products.list.query({ limit: 1000 }),
    enabled: domain === 'FG' || domain === 'WIP' || domain === 'SCRAP',
  });
  const { data: toolingsData } = useQuery({
    // Danh mục CCDC đầy đủ (listItemsPaginated giới hạn 100/trang: pageSize 1000 bị máy chủ từ chối → danh mục trống)
    queryKey: ['tooling', 'listItems'],
    queryFn: () => trpcClient.tooling.listItems.query(),
    enabled: domain === 'TECH',
  });

  const availableItems = useMemo(() => {
    const items: any[] = [];
    if (domain === 'MATERIAL' || domain === 'SCRAP') {
      items.push(...(materialsData?.items || []).map((m: any) => ({
        ...m,
        id: m.id,
        code: String(m.materialId || m.code || m.id || '—').trim(),
        name: cleanMaterialDisplayName(m.name || m.materialId),
        unit: m.unit || 'Tờ',
      })));
    }
    if (domain === 'FG' || domain === 'WIP' || domain === 'SCRAP') {
      items.push(...(productsData?.items || []).map((p: any) => ({
        ...p,
        id: p.id,
        code: String(p.productCode || p.productName || p.code || p.id || '—').trim(),
        name: p.name || p.productName || '—',
        unit: p.unit || 'Cái',
      })));
    }
    if (domain === 'TECH') {
      items.push(...((toolingsData as any[]) || []).map((t: any) => ({
        ...t,
        id: t.id,
        code: String(t.code || t.moldCode || t.plateCode || t.dieCutId || t.id || '—').trim(),
        name: t.name || t.code || '—',
        unit: t.unit || 'Bộ',
      })));
    }
    return items;
  }, [domain, materialsData, productsData, toolingsData]);

  const selectedItem = useMemo(
    () => availableItems.find((i: any) => i.id === localItemId),
    [availableItems, localItemId],
  );

  // 3. Fetch Balance (Opening Balance)
  const { data: balanceResult, isLoading: isLoadingBalance } = useQuery({
    queryKey: ['inventory', 'getBalanceByItemId', {
      itemId: localItemId,
      warehouseId: warehouseId === 'ALL' ? undefined : warehouseId,
    }],
    queryFn: () => trpcClient.inventory.getBalanceByItemId.query({
      itemId: localItemId,
      warehouseId: warehouseId === 'ALL' ? undefined : warehouseId,
    }),
    enabled: !!localItemId,
  });
  const balanceData: any = balanceResult;

  // 4. Fetch Stock Card History
  const { data: txResult, isLoading: isLoadingTx } = useQuery({
    queryKey: ['inventory', 'getStockCard', {
      itemId: localItemId,
      itemType: itemType as any,
      warehouseId: warehouseId === 'ALL' ? undefined : warehouseId,
      startDate: startDate ? new Date(startDate).toISOString() : undefined,
      endDate: endDate ? new Date(endDate).toISOString() : undefined,
    }],
    queryFn: () => trpcClient.inventory.getStockCard.query({
      itemId: localItemId,
      itemType: itemType as any,
      warehouseId: warehouseId === 'ALL' ? undefined : warehouseId,
      startDate: startDate ? new Date(startDate).toISOString() : undefined,
      endDate: endDate ? new Date(endDate).toISOString() : undefined,
    }),
    enabled: !!localItemId && !!itemType,
  });
  const txData: any = txResult;
  const rawTxs: any[] = txData?.data || [];

  const filteredTxs = useMemo(() => {
    if (!headerSearch.trim()) return rawTxs;
    const q = headerSearch.toLowerCase().trim();
    return rawTxs.filter((tx: any) =>
      tx.transactionNo?.toLowerCase().includes(q) ||
      (tx.notes || tx.description || '')?.toLowerCase().includes(q) ||
      tx.lotNumber?.toLowerCase().includes(q)
    );
  }, [rawTxs, headerSearch]);

  const pagedTxs = useMemo(() => {
    return filteredTxs.slice((page - 1) * pageSize, page * pageSize);
  }, [filteredTxs, page, pageSize]);

  const handleExport = () => {
    if (!txData?.data) return;
    const itemCode = selectedItem?.code || balanceData?.material?.code || balanceData?.product?.code || balanceData?.itemCode || localItemId;
    const openingBal = txData?.summary?.beginningBalance ?? txData?.openingBalance ?? 0;

    const formattedData = [
      {
        idx: '-',
        transactionDate: '-',
        documentNo: '-',
        notes: 'SỐ DƯ ĐẦU KỲ',
        inQty: '',
        outQty: '',
        balance: openingBal,
      },
      ...txData.data.map((tx: any, idx: number) => {
        const isInc = tx.txType === 'IN' || tx.type === 'RECEIPT' || (tx.type === 'ADJUSTMENT' && tx.quantity > 0);
        return {
          idx: idx + 1,
          transactionDate: tx.txDate ? new Date(tx.txDate).toLocaleDateString('vi-VN') : '',
          documentNo: tx.transactionNo || '-',
          notes: tx.notes || tx.description || (isInc ? 'Nhập kho' : 'Xuất kho'),
          inQty: isInc ? tx.quantity : '',
          outQty: !isInc ? Math.abs(tx.quantity) : '',
          balance: tx.runningBalance ?? tx.balanceAfter,
        };
      }),
    ];

    exportToExcel(formattedData, STOCK_CARD_COLUMNS, `TheKho_${itemCode}_${Date.now()}`);
  };

  const displayCode =
    selectedItem?.code ||
    balanceData?.material?.code ||
    balanceData?.product?.code ||
    balanceData?.itemCode ||
    '—';
  const displayName =
    selectedItem?.name ||
    balanceData?.material?.name ||
    balanceData?.product?.name ||
    balanceData?.itemName ||
    (isLoadingBalance ? 'Đang tải...' : '—');
  const displayUnit =
    selectedItem?.unit ||
    balanceData?.material?.unit ||
    balanceData?.product?.unit ||
    balanceData?.unit ||
    '-';
  const displayOpeningBalance =
    txData?.summary?.beginningBalance ?? txData?.openingBalance ?? 0;

  return (
    <ListPageShell
      unitLabel="giao dịch thẻ kho"
      totalItems={filteredTxs.length}
      currentPage={page}
      pageSize={pageSize}
      onPageChange={setPage}
      onPageSizeChange={(newSize) => {
        setPageSize(newSize);
        setPage(1);
      }}
      isLoading={isLoadingTx || isLoadingBalance}
      topBanner={
        localItemId ? (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-2 mb-2">
            <div className="md:col-span-3 bg-white p-2.5 rounded-lg border border-slate-200 shadow-2xs flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 bg-blue-50 text-blue-600 rounded-full flex items-center justify-center font-bold text-xs shrink-0">
                  {displayCode !== '—' ? displayCode.substring(0, 2) : 'SP'}
                </div>
                <div>
                  <div className="font-bold text-sm text-slate-900 line-clamp-1">
                    {displayName}
                  </div>
                  <div className="text-[11px] text-slate-500 mt-0.5">
                    Mã: <span className="font-semibold text-slate-700">{displayCode}</span>
                    <span className="mx-2">|</span>
                    ĐVT: <span className="font-semibold text-slate-700">{displayUnit}</span>
                    {/* Quy đổi ĐVT phụ (04/11/2026) */}
                    {balanceData?.material?.secondaryUnit && packingLabel(balanceData.material) ? (
                      <>
                        <span className="mx-2">|</span>
                        <span className="font-semibold text-slate-700" data-testid="stock-card-packing">{packingLabel(balanceData.material)}</span>
                      </>
                    ) : null}
                  </div>
                </div>
              </div>
            </div>
            <div className="bg-blue-600 text-white p-2.5 rounded-lg border border-blue-700 shadow-2xs flex items-center justify-between px-4">
              <div className="text-blue-100 text-xs font-medium">Tồn đầu kỳ:</div>
              <div className="text-xl font-bold font-mono">
                {displayOpeningBalance}
              </div>
            </div>
          </div>
        ) : undefined
      }
      headerFilters={
        <div className="flex flex-wrap items-center gap-2">
          {/* Item Selector Combobox */}
          <Popover open={openCombobox} onOpenChange={setOpenCombobox}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                role="combobox"
                aria-expanded={openCombobox}
                className="w-[240px] sm:w-[280px] justify-between h-7 text-xs bg-slate-50 border-slate-200"
              >
                <span className="truncate">
                  {localItemId && availableItems.length > 0
                    ? (() => {
                        const it = availableItems.find((i: any) => i.id === localItemId);
                        if (!it) return "Chọn vật tư/sản phẩm...";
                        return `${it.code} - ${it.name}`;
                      })()
                    : "Chọn vật tư/sản phẩm..."}
                </span>
                <ChevronsUpDown className="ml-1 h-3.5 w-3.5 shrink-0 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[360px] p-0" align="start">
              <Command>
                <CommandInput placeholder="Tìm mã hoặc tên..." className="h-8 text-xs" />
                <CommandList>
                  <CommandEmpty>Không tìm thấy.</CommandEmpty>
                  <CommandGroup>
                    {availableItems.map((item: any) => (
                      <CommandItem
                        key={item.id}
                        value={`${item.code} ${item.name}`}
                        onSelect={() => {
                          setLocalItemId(item.id);
                          setOpenCombobox(false);
                        }}
                      >
                        <Check
                          className={cn(
                            "mr-2 h-3.5 w-3.5",
                            localItemId === item.id ? "opacity-100" : "opacity-0"
                          )}
                        />
                        <div className="flex flex-col min-w-0">
                          <span className="font-semibold text-xs">{item.code}</span>
                          <span className="text-[10px] text-slate-500 truncate">{item.name}</span>
                        </div>
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>

          {/* Date Filters */}
          <div className="flex items-center gap-1.5 bg-slate-50 border border-slate-200 rounded-md px-2 py-0.5">
            <Calendar className="w-3.5 h-3.5 text-slate-400" />
            <Input 
              type="date" 
              value={startDate} 
              onChange={(e) => setStartDate(e.target.value)}
              className="w-28 h-6 text-xs bg-transparent border-none p-0 focus-visible:ring-0"
            />
            <span className="text-slate-400 text-xs">-</span>
            <Input 
              type="date" 
              value={endDate} 
              onChange={(e) => setEndDate(e.target.value)}
              className="w-28 h-6 text-xs bg-transparent border-none p-0 focus-visible:ring-0"
            />
          </div>

          {/* Warehouse Filter */}
          <Select value={warehouseId} onValueChange={setWarehouseId}>
            <SelectTrigger className="w-[140px] h-7 text-xs bg-slate-50 border-slate-200">
              <SelectValue placeholder="Tất cả kho" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">Tất cả kho</SelectItem>
              {warehouses.map((w: any) => (
                <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      }
      actions={
        <div className="flex items-center gap-1.5">
          {localItemId && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPrintOpen(true)}
              className="h-7 text-xs gap-1.5"
              data-testid="stock-card-print-btn"
            >
              <Printer className="w-3.5 h-3.5" />
              In / PDF
            </Button>
          )}
          {localItemId && (
            <Button
              variant="default"
              size="sm"
              onClick={handleExport}
              className="h-7 text-xs gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white font-medium"
            >
              <Download className="w-3.5 h-3.5" />
              Xuất Excel
            </Button>
          )}
        </div>
      }
    >
      {printOpen && localItemId && (
        <PrintDialog
          open={printOpen}
          onClose={() => setPrintOpen(false)}
          type="custom"
          orientation="landscape"
          title={`Sổ kho ${displayCode}`}
          documentTitle={`SoKho_${displayCode}`}
        >
          <StockCardPrintTemplate
            warehouseLabel={
              warehouseId === 'ALL'
                ? `${domainDef.label} — tất cả kho`
                : warehouses.find((w: any) => w.id === warehouseId)?.name || domainDef.label
            }
            itemCode={displayCode}
            itemName={displayName}
            unit={displayUnit}
            startDate={startDate}
            endDate={endDate}
            openingBalance={Number(displayOpeningBalance) || 0}
            rows={filteredTxs.map((tx: any) => {
              const isInc = tx.txType === 'IN' || tx.type === 'RECEIPT' || (tx.type === 'ADJUSTMENT' && tx.quantity > 0);
              return {
                date: tx.txDate ? new Date(tx.txDate).toLocaleDateString('vi-VN') : '',
                documentNo: tx.transactionNo || tx.documentNo || '-',
                notes: tx.notes || tx.description || (isInc ? 'Nhập kho' : 'Xuất kho'),
                warehouseName: tx.warehouse?.name || '',
                inQty: isInc ? Math.abs(Number(tx.quantity) || 0) : null,
                outQty: !isInc ? Math.abs(Number(tx.quantity) || 0) : null,
                balance: Number(tx.runningBalance ?? tx.balanceAfter ?? 0),
              };
            })}
          />
        </PrintDialog>
      )}
      {!localItemId ? (
        <div className="bg-slate-50 border border-dashed rounded-lg p-12 text-center text-slate-500 my-4">
          <Box className="w-8 h-8 text-slate-400 mx-auto mb-2 opacity-60" />
          <div className="text-sm font-semibold text-slate-700">Chưa chọn Hàng hóa</div>
          <p className="text-xs text-slate-400 mt-1">Vui lòng chọn vật tư hoặc sản phẩm ở bộ lọc phía trên để xem Thẻ kho cầm tay.</p>
        </div>
      ) : (
        <Table className="apv-compact-table min-w-[950px]">
          <TableHeader className="apv-table-header">
            <TableRow>
              <TableHead className="w-[140px]">Ngày</TableHead>
              <TableHead className="w-[140px]">Mã CT/Phiếu</TableHead>
              <TableHead className="w-[100px]">Loại GD</TableHead>
              <TableHead className="min-w-[200px]">Diễn Giải</TableHead>
              <TableHead className="w-[110px] text-right">SL Nhập</TableHead>
              <TableHead className="w-[110px] text-right">SL Xuất</TableHead>
              <TableHead className="w-[120px] text-right">Tồn Lũy Kế</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoadingTx || isLoadingBalance ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-10 text-slate-500">Đang tải lịch sử giao dịch thẻ kho...</TableCell>
              </TableRow>
            ) : pagedTxs.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-10 text-slate-500">Không có giao dịch nào trong khoảng thời gian đã chọn.</TableCell>
              </TableRow>
            ) : (
              pagedTxs.map((row: any) => {
                const isReceipt = row.txType === 'IN' || row.type === 'RECEIPT';
                const isIssue = row.txType === 'OUT' || row.type === 'ISSUE';

                return (
                  <TableRow key={row.id} className="hover:bg-slate-50/80">
                    <TableCell className="font-mono text-xs text-slate-600">
                      {row.txDate ? format(new Date(row.txDate), 'dd/MM/yyyy HH:mm') : '-'}
                    </TableCell>
                    <TableCell className="font-mono text-xs font-semibold text-indigo-700">
                      {row.docId ? (
                        <Link
                          to={`/warehouse/${warehouseType || 'material'}/transactions/$id` as any}
                          params={{ id: row.docId } as any}
                          className="hover:underline cursor-pointer"
                          title="Xem phiếu kho gốc"
                        >
                          {row.referenceNo || row.transactionNo}
                        </Link>
                      ) : (
                        row.referenceNo || row.transactionNo
                      )}
                    </TableCell>
                    <TableCell>
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                        isReceipt ? 'bg-emerald-100 text-emerald-800' : isIssue ? 'bg-rose-100 text-rose-800' : 'bg-slate-100 text-slate-800'
                      }`}>
                        {isReceipt ? 'Nhập kho' : isIssue ? 'Xuất kho' : 'Điều chỉnh'}
                      </span>
                    </TableCell>
                    <TableCell className="text-xs text-slate-700">
                      {row.notes || row.description || '-'}
                    </TableCell>
                    <TableCell className="text-right font-mono text-xs font-semibold text-emerald-600">
                      {isReceipt ? `+${row.quantity}` : '-'}
                    </TableCell>
                    <TableCell className="text-right font-mono text-xs font-semibold text-rose-600">
                      {isIssue ? `-${row.quantity}` : '-'}
                    </TableCell>
                    <TableCell className="text-right font-mono text-xs font-bold text-slate-900">
                      {row.runningBalance ?? row.balanceAfter}
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      )}
    </ListPageShell>
  );
}
